import { useTranslation } from "@repo/i18n";
import { formatSlotTime } from "../format";
import type { SelectedSlot } from "../flow/state";

export interface DisplaySlot {
  serviceId: string;
  startSec: number;
  durationSec: number;
  /** Whether the slot can still be booked. A fully-booked slot is rendered as a DISABLED option whose
   * accessible name announces the "complet" state — never omitted, so a screen reader hears the state
   * rather than silence (features/accessibility.md R4 + spec edge case). Defaults to available. */
  available?: boolean;
}

/**
 * The ONE list component for both open slots and alternatives, and for both slot-full and covers-cap
 * responses (R8: they render byte-identical). Slots are shown EXACTLY as returned — the widget derives
 * no times (AV-6/AV-8). Each slot is a ≥44px button labelled with its venue-local time; the one whose
 * hold is being placed shows a busy state and the whole list disables so no double-hold races. A slot
 * flagged unavailable renders disabled with a "complet" accessible name (R4).
 */
export function SlotList({
  slots,
  onPick,
  timeZone,
  locale,
  busyStartSec,
  disabled,
  labelledBy,
}: {
  slots: DisplaySlot[];
  onPick: (slot: SelectedSlot) => void;
  timeZone: string;
  locale: string;
  busyStartSec: number | null;
  disabled: boolean;
  labelledBy?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="rsv-slots" aria-labelledby={labelledBy}>
      {slots.map((slot) => {
        const time = formatSlotTime(slot.startSec, timeZone, locale);
        const busy = busyStartSec === slot.startSec;
        const unavailable = slot.available === false;
        return (
          <button
            key={`${slot.serviceId}-${slot.startSec}`}
            type="button"
            className="rsv-slot"
            disabled={disabled || busy || unavailable}
            aria-disabled={unavailable || undefined}
            aria-label={
              unavailable
                ? t("widget.booking.slots.full", { time })
                : t("widget.booking.slots.pick", { time })
            }
            onClick={
              unavailable
                ? undefined
                : () =>
                    onPick({
                      serviceId: slot.serviceId,
                      startSec: slot.startSec,
                      durationSec: slot.durationSec,
                    })
            }
          >
            {busy ? t("widget.booking.slots.holding") : time}
          </button>
        );
      })}
    </div>
  );
}
